import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wordmark } from "@/components/landing/Wordmark";
import { Loader2, ShieldCheck } from "lucide-react";
import { authClient } from "@/lib/auth/auth-client";
import { getMyRoles } from "@/server/auth-access.functions";

export const Route = createFileRoute("/admin-login")({
 head: () => ({ meta: [{ name: "robots", content: "noindex,nofollow" }] }),
 component: AdminLoginPage,
});

function AdminLoginPage() {
 const navigate = useNavigate();
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);

 // If already signed in as admin, redirect immediately
 useEffect(() => {
 let cancelled = false;
 (async () => {
 try {
 const { data } = await authClient.getSession();
 if (!data?.user || cancelled) return;
 const roles = await getMyRoles();
 if (cancelled) return;
 if (roles.includes("admin")) {
 navigate({ to: "/dashboard/admin" });
 }
 } catch { /* ignore */ }
 })();
 return () => { cancelled = true; };
 }, [navigate]);

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);
 setLoading(true);
 try {
 const { error: signInError, data: signInData } = await authClient.signIn.email({ email, password });
 if (signInError) {
 setError("Invalid credentials.");
 return;
 }
 if (signInData?.twoFactorRedirect) return; // second-factor challenge in progress
 const roles = await getMyRoles();
 if (!roles.includes("admin")) {
 await authClient.signOut();
 setError("This account does not have admin access.");
 return;
 }
 navigate({ to: "/dashboard/admin" });
 } catch {
 setError("Something went wrong. Please try again.");
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
 <div className="w-full max-w-md">
 <div className="flex flex-col items-center gap-3 mb-6">
 <Wordmark className="text-2xl" />
 <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-muted-foreground">
 <ShieldCheck className="h-4 w-4" />
 Admin Access
 </div>
 </div>

 <div className="border border-border bg-card p-6 shadow-sm space-y-5">
 <div>
 <h1 className="text-lg font-semibold">Sign in to admin</h1>
 <p className="text-xs text-muted-foreground mt-1">
 Restricted area. Admin accounts are provisioned manually, there is no sign-up.
 </p>
 </div>

 <form onSubmit={handleSubmit} className="space-y-3">
 <div className="space-y-1.5">
 <Label htmlFor="admin-email" className="text-xs">Email</Label>
 <Input
 id="admin-email"
 type="email"
 autoComplete="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 required
 />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="admin-password" className="text-xs">Password</Label>
 <Input
 id="admin-password"
 type="password"
 autoComplete="current-password"
 value={password}
 onChange={(e) => setPassword(e.target.value)}
 required
 minLength={6}
 />
 </div>
 <Button type="submit" className="w-full gap-2" disabled={loading}>
 {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
 Sign in
 </Button>
 </form>

 {error && <p className="text-sm text-destructive text-center">{error}</p>}
 </div>

 <p className="mt-6 text-center text-xs text-muted-foreground">
 <Link to="/" className="hover:text-foreground">← Back to home</Link>
 </p>
 </div>
 </div>
 );
}
